// src/components/DesignationForm.tsx
import React, { useState, useEffect } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { Designation } from '../types/designation';

interface DesignationFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  initialData?: Designation | null;
  isLoading?: boolean;
  /** Lower-cased, trimmed names of designations that already exist
   *  (excluding the one being edited). Used for the duplicate guard. */
  existingNames?: string[];
}

export function DesignationForm({ isOpen, onClose, onSubmit, initialData, isLoading, existingNames = [] }: DesignationFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [nameError, setNameError] = useState<string | null>(null);
  const [descError, setDescError] = useState<string | null>(null);

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || ''
      });
    } else {
      setFormData({
        name: '',
        description: ''
      });
    }
    setNameError(null);
    setDescError(null);
  }, [initialData, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'name' && nameError) setNameError(null);
    if (name === 'description' && descError) setDescError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = formData.name.trim();
    if (!trimmed) {
      setNameError('Designation title is required');
      return;
    }
    // Editing keeps its own name; only a CHANGED name is checked for duplicates.
    // (For a system default, an unchanged name is expected — the save forks a
    // private copy server-side, it doesn't clash.)
    const originalName = (initialData?.name || '').trim().toLowerCase();
    if (
      trimmed.toLowerCase() !== originalName &&
      existingNames.includes(trimmed.toLowerCase())
    ) {
      setNameError(`A designation titled "${trimmed}" already exists`);
      return;
    }
    // Roles & Responsibilities is mandatory — it feeds the offer / joining letters.
    const trimmedDesc = formData.description.trim();
    if (!trimmedDesc) {
      setDescError('Roles and Responsibilities is required');
      return;
    }
    onSubmit({ ...formData, name: trimmed, description: trimmedDesc });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-in fade-in zoom-in duration-300">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {initialData ? 'Edit Designation' : 'Add New Designation'}
            </h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Designation Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              aria-invalid={!!nameError}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors ${
                nameError
                  ? 'border-red-400 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder="e.g., Software Engineer, HR Manager, Sales Executive"
              autoFocus
            />
            {nameError && (
              <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                {nameError}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Roles and Responsibilities <span className="text-red-500">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={5}
              aria-invalid={!!descError}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors resize-none ${
                descError
                  ? 'border-red-400 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder={"List the roles and responsibilities — one per line, or separated by semicolons.\ne.g.\nWrite clean, maintainable code\nCollaborate with the product team\nReview pull requests"}
            />
            {descError ? (
              <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                {descError}
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                Used as the Key Responsibilities in the employee's Offer &amp; Joining letters.
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Saving...' : initialData ? 'Update Designation' : 'Create Designation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}